/**
 * VaultCampaign — v3.99d.369
 *
 * All UI for the "Build the Vault" market-change campaign lives in
 * this file so it's easy to review + iterate. Every string, date,
 * badge, and CTA is fetched from /api/vault-campaign so admins can
 * update wording without a code deploy.
 *
 * Exports:
 *   • useVaultCampaign()      — hook returning {campaign, loading}
 *   • VaultBanner             — compact top-of-page banner
 *   • VaultCountdown          — timezone-safe live countdown
 *   • VaultAuctionUrgencyLine — short line above auction listings
 *   • VaultBadge              — small chip for listing cards
 *   • VaultLandingSection     — full "Preserve what you don't want to
 *                                 lose" section for /build-the-vault
 *   • VAULT_BADGE_ALLOWLIST   — client-side allowlist used by admins
 */

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Vault, Clock, AlertTriangle, ArrowRight, ChevronDown, ChevronUp, Info } from "lucide-react";
import { api } from "../lib/apiClient";

/**
 * Linkify — v3.99d.371
 *
 * Turns bare URLs inside admin-editable campaign copy into clickable
 * links without allowing arbitrary HTML injection. We split the text
 * on a conservative http/https regex and wrap only the URL fragments
 * in <a> tags. Plain text stays plain text.
 */
const URL_RE = /(https?:\/\/[^\s<>"']+)/g;
const URL_TEST_RE = /^https?:\/\/[^\s<>"']+$/;
function Linkify({ text, className }) {
  if (!text) return null;
  const parts = String(text).split(URL_RE);
  return (
    <span className={className}>
      {parts.map((part, i) => {
        if (part && URL_TEST_RE.test(part)) {
          return (
            <a key={i} href={part} target="_blank" rel="noopener noreferrer"
                className="underline hover:opacity-80"
                style={{ color: "var(--gold)" }}
                data-testid="vault-linkified-url">
              {part}
            </a>
          );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </span>
  );
}

export const VAULT_BADGE_ALLOWLIST = [
  "BUILD THE VAULT",
  "LIMITED GENETICS",
  "UNLIKELY TO RETURN",
  "FINAL BREEDER INVENTORY",
  "DISCONTINUED LINE",
  "LAST PACKS",
];

// Cache the /vault-campaign response for the tab lifetime so we're
// not slamming the endpoint from three components.
let _cache = null;
let _cacheAt = 0;

export function useVaultCampaign() {
  const [state, setState] = useState({ campaign: _cache, loading: !_cache });
  useEffect(() => {
    let cancelled = false;
    const stale = !_cache || (Date.now() - _cacheAt) > 60000;
    if (!stale) return;
    (async () => {
      try {
        const r = await api.get("/vault-campaign");
        if (cancelled) return;
        _cache = r.data;
        _cacheAt = Date.now();
        setState({ campaign: r.data, loading: false });
      } catch {
        if (!cancelled) setState((s) => ({ ...s, loading: false }));
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return state;
}

/**
 * VaultCountdown — real, timezone-safe. Never negative — flips to a
 * neutral post-deadline note when the target passes. Admins can hide
 * the whole countdown via `campaign.countdown_enabled=false`.
 */
export function VaultCountdown({ campaign, compact = false }) {
  const target = useMemo(() => {
    if (!campaign?.effective_date) return null;
    const d = new Date(campaign.effective_date);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [campaign?.effective_date]);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [target]);

  if (!campaign?.countdown_enabled || !target) return null;
  const diff = target.getTime() - now.getTime();
  if (diff <= 0) {
    return (
      <div className="text-[11px]"
           style={{ color: "var(--text-muted)" }}
           data-testid="vault-countdown-post-deadline">
        {campaign.post_deadline_note}
      </div>
    );
  }
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);
  const secs = Math.floor((diff % 60_000) / 1_000);
  const cells = [
    { n: days, l: "days" },
    { n: hours, l: "hrs" },
    { n: mins, l: "min" },
    { n: secs, l: "sec" },
  ];
  return (
    <div className={`inline-flex ${compact ? "gap-1.5" : "gap-2"} items-center flex-wrap`}
         data-testid="vault-countdown">
      {cells.map((c) => (
        <div key={c.l}
             className="flex flex-col items-center"
             style={{ minWidth: 40 }}>
          <span className={compact ? "font-mono text-sm font-bold" : "font-mono text-xl font-bold"}
                style={{ color: "var(--gold)" }}>
            {String(c.n).padStart(2, "0")}
          </span>
          <span className="text-[9px] font-mono uppercase tracking-wider"
                style={{ color: "var(--text-muted)" }}>
            {c.l}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * VaultBanner — impossible-to-miss but not panic-red. Mounted on
 * homepage + auctions page. Renders nothing when the admin toggles
 * `campaign.enabled=false`.
 */
export function VaultBanner({ variant = "default" }) {
  const { campaign } = useVaultCampaign();
  if (!campaign?.enabled) return null;
  // v3.99d.405 — Recolor to red-urgency so the closing-window banner
  // reads as a genuine deadline instead of another gold promo. Also
  // tightens the mobile body copy: prior banner rendered the full 3-4
  // sentence body inline; now we clip to a single lede on <sm and
  // link to the full explainer.
  return (
    <section
      className="relative overflow-hidden border"
      style={{ background: "linear-gradient(135deg, rgba(200,60,50,0.14) 0%, rgba(23,26,31,0.6) 100%)",
                borderColor: "rgb(200,60,50)",
                borderRadius: 2 }}
      data-testid={`vault-banner-${variant}`}>
      <div className="absolute top-0 left-0 right-0 h-[2px]"
           style={{ background: "linear-gradient(90deg, transparent, rgb(200,60,50), transparent)" }}/>
      <div className="px-4 sm:px-5 py-4 sm:py-5 flex items-start gap-4 flex-wrap">
        <div className="flex-shrink-0 w-11 h-11 flex items-center justify-center"
             style={{ background: "rgb(200,60,50)",
                      color: "#fff",
                      borderRadius: 2 }}>
          <Vault className="w-5 h-5"/>
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] mb-1 inline-flex items-center gap-1.5"
               style={{ color: "rgb(220,90,80)" }}>
            <AlertTriangle className="w-3 h-3"/>
            Closing window
          </div>
          <div className="font-serif tracking-tighter leading-tight"
               style={{ color: "var(--text)",
                        fontSize: "clamp(1.15rem, 2.6vw, 1.6rem)" }}
               data-testid="vault-banner-headline">
            {campaign.headline}
          </div>
          <p className="text-[13px] mt-2 leading-relaxed max-w-2xl whitespace-pre-wrap line-clamp-2 sm:line-clamp-none"
             style={{ color: "var(--text-2)" }}
             data-testid="vault-banner-body">
            <Linkify text={campaign.body} />
          </p>
        </div>
        <div className="flex flex-col items-end gap-2 min-w-[180px]">
          <VaultCountdown campaign={campaign} compact />
          <div className="text-[9px] font-mono uppercase tracking-wider text-right max-w-[220px]"
                style={{ color: "var(--text-muted)" }}>
            {campaign.countdown_label}
          </div>
        </div>
      </div>
      <div className="border-t px-4 sm:px-5 py-3 flex items-center gap-2 flex-wrap"
           style={{ borderColor: "rgba(200,60,50,0.35)" }}>
        <Link to={campaign.primary_cta_href || "/build-the-vault"}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[10px] font-mono uppercase tracking-[0.18em] transition hover:scale-[1.02]"
              style={{ background: "var(--gold)",
                        color: "var(--bg)",
                        borderRadius: 2 }}
              data-testid="vault-banner-primary-cta">
          {campaign.primary_cta_label || "BUILD YOUR VAULT"}
          <ArrowRight className="w-3 h-3"/>
        </Link>
        <Link to={campaign.secondary_cta_href || "/auctions"}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] font-mono uppercase tracking-[0.18em] hover:opacity-80"
              style={{ border: "1px solid var(--gold)",
                        color: "var(--gold)",
                        borderRadius: 2 }}
              data-testid="vault-banner-secondary-cta">
          {campaign.secondary_cta_label || "VIEW ENDING SOON"}
        </Link>
        <div className="ml-auto text-[10px] flex items-center gap-1"
              style={{ color: "var(--text-muted)" }}>
          <Info className="w-3 h-3"/>
          {campaign.disclaimer}
        </div>
      </div>
    </section>
  );
}

/**
 * VaultAuctionUrgencyLine — small strip mounted on the auctions page
 * near the listings with a "Why this matters" disclosure.
 */
export function VaultAuctionUrgencyLine() {
  const { campaign } = useVaultCampaign();
  const [open, setOpen] = useState(false);
  if (!campaign?.enabled) return null;
  return (
    <div className="mb-4 border"
         style={{ borderColor: "var(--border)",
                   background: "rgba(212,175,55,0.04)",
                   borderRadius: 2 }}
         data-testid="vault-auction-urgency-line">
      <button type="button"
               onClick={() => setOpen((v) => !v)}
               className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left">
        <div className="flex items-center gap-2 min-w-0">
          <Vault className="w-3.5 h-3.5 flex-shrink-0"
                 style={{ color: "var(--gold)" }}/>
          <div className="text-[12px] leading-snug truncate"
               style={{ color: "var(--text-2)" }}>
            {campaign.auction_line}
          </div>
        </div>
        <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider flex-shrink-0"
              style={{ color: "var(--gold)" }}>
          Why this matters
          {open ? <ChevronUp className="w-3 h-3"/> : <ChevronDown className="w-3 h-3"/>}
        </div>
      </button>
      {open && (
        <div className="border-t px-3 py-3 text-[12px] leading-relaxed"
             style={{ borderColor: "var(--border)",
                       color: "var(--text-2)" }}
             data-testid="vault-auction-urgency-expanded">
          <p>{campaign.why_this_matters}</p>
          <p className="mt-2 text-[10px]"
             style={{ color: "var(--text-muted)" }}>
            {campaign.disclaimer}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * VaultBadge — small chip rendered on a listing card when the listing
 * has ADMIN-set vault_badges. NEVER auto-applied.
 */
export function VaultBadge({ label }) {
  if (!label || !VAULT_BADGE_ALLOWLIST.includes(label) && label !== "ENDING SOON") {
    return null;
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5"
      style={{ background: "var(--gold)",
                color: "var(--bg)",
                borderRadius: 2 }}
      data-testid={`vault-badge-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <Vault className="w-2.5 h-2.5"/>
      {label}
    </span>
  );
}

/**
 * VaultLandingSection — used by the dedicated /build-the-vault route
 * and can also be dropped inline on other pages. Pulls truthful picks
 * from /vault-campaign/picks (ending-soon auctions, low-stock listings,
 * admin-curated). Renders NOTHING when there are no truthful picks
 * (never invents scarcity).
 */
export function VaultLandingSection() {
  const { campaign } = useVaultCampaign();
  const [picks, setPicks] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/vault-campaign/picks");
        if (!cancelled) setPicks(r.data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);
  if (!campaign?.enabled) return null;
  const groups = [
    { key: "admin_curated", label: "Admin-curated · Vault picks",
      rows: picks?.admin_curated || [] },
    { key: "ending_soon", label: "Ending soon (next 72h)",
      rows: picks?.ending_soon || [] },
    { key: "low_stock", label: "Low stock (≤3 remaining)",
      rows: picks?.low_stock || [] },
  ];
  const allEmpty = groups.every((g) => g.rows.length === 0);

  return (
    <section className="max-w-[1200px] mx-auto px-6 py-14"
             data-testid="vault-landing-section">
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] inline-flex items-center gap-1.5 mb-3"
           style={{ color: "var(--gold)" }}>
        <Vault className="w-3 h-3"/>
        Build the Vault
      </div>
      <h2 className="font-serif tracking-tighter leading-[0.95] mb-4"
          style={{ color: "var(--text)",
                    fontSize: "clamp(2.25rem, 5vw, 3.75rem)" }}
          data-testid="vault-landing-headline">
        {campaign.hero_headline}
      </h2>
      <p className="text-base max-w-3xl leading-relaxed mb-6 whitespace-pre-wrap"
         style={{ color: "var(--text-2)" }}
         data-testid="vault-landing-body">
        <Linkify text={campaign.hero_body} />
      </p>

      <div className="mb-8 border p-4 flex items-center gap-4 flex-wrap"
           style={{ borderColor: "var(--gold)",
                     background: "rgba(212,175,55,0.05)",
                     borderRadius: 2 }}>
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] font-mono uppercase tracking-[0.22em]"
               style={{ color: "var(--gold)" }}>
            {campaign.countdown_label}
          </div>
          <VaultCountdown campaign={campaign}/>
        </div>
        <Link to={campaign.primary_cta_href || "/auctions"}
               className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[10px] font-mono uppercase tracking-[0.18em]"
               style={{ background: "var(--gold)",
                         color: "var(--bg)",
                         borderRadius: 2 }}
               data-testid="vault-landing-primary-cta">
          {campaign.primary_cta_label}
          <ArrowRight className="w-3 h-3"/>
        </Link>
      </div>

      {allEmpty ? (
        <div className="border p-8 text-center text-sm"
             style={{ borderColor: "var(--border)",
                       color: "var(--text-muted)",
                       borderRadius: 2 }}
             data-testid="vault-landing-empty">
          No qualifying vault picks are available right now. Check
          the auction and shop pages for the latest inventory.
        </div>
      ) : (
        groups.map((g) => (
          g.rows.length > 0 && (
            <div key={g.key} className="mb-6"
                  data-testid={`vault-picks-group-${g.key}`}>
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] mb-2"
                    style={{ color: "var(--text-muted)" }}>
                {g.label}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {g.rows.slice(0, 9).map((r) => (
                  <VaultPickRow key={r.id} row={r}/>
                ))}
              </div>
            </div>
          )
        ))
      )}

      <p className="mt-8 text-[11px] leading-relaxed max-w-3xl"
         style={{ color: "var(--text-muted)" }}
         data-testid="vault-landing-disclaimer">
        {campaign.disclaimer}
      </p>
    </section>
  );
}

function VaultPickRow({ row }) {
  const thumb = row.image_url
    || (Array.isArray(row.images) && row.images[0]);
  const priceStr = row.listing_type === "auction"
    ? `$${Number(row.current_bid || row.starting_bid || 0).toFixed(0)}`
    : `$${Number(row.price || 0).toFixed(0)}`;
  const stockNote = row.quantity && row.quantity <= 3
    ? `Only ${row.quantity} left`
    : (row.auction_ends_at ? "Auction ends soon" : "");
  return (
    <Link to={`/listing/${row.id}`}
          className="flex items-center gap-2.5 border p-2 hover:bg-[var(--bg-2)] transition"
          style={{ borderColor: "var(--border)", borderRadius: 2 }}
          data-testid={`vault-pick-row-${row.id}`}>
      {thumb ? (
        <img src={thumb} alt=""
              className="w-12 h-12 object-cover flex-shrink-0"
              style={{ borderRadius: 2 }}
              loading="lazy"/>
      ) : (
        <div className="w-12 h-12 flex items-center justify-center flex-shrink-0"
              style={{ background: "var(--bg-2)", borderRadius: 2 }}>
          <Vault className="w-4 h-4" style={{ color: "var(--gold)" }}/>
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold truncate"
              style={{ color: "var(--text)" }}>
          {row.title}
        </div>
        <div className="text-[11px] font-mono"
              style={{ color: "var(--gold)" }}>
          {priceStr}
          {stockNote && (
            <span className="ml-1.5"
                   style={{ color: "var(--text-muted)" }}>
              · {stockNote}
            </span>
          )}
        </div>
        {Array.isArray(row.vault_badges) && row.vault_badges.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {row.vault_badges.slice(0, 2).map((b) => (
              <VaultBadge key={b} label={b}/>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}
